import {
  hitEscape,
  receiveDocumentHeaders,
  newNotificationCounts,
  documentClick,
} from '../actions';
import { addEventListener, document } from '../env/browser';
import { getDocumentUrl } from '../utils/url';
import { fetchHeaders } from '../utils/fetch';
import {
  CHECK_FOR_UPDATES_INTERVAL,
  CHECK_FOR_NOTIFICATION_COUNT_INTERVAL,
} from '../constants';
import type { AppDispatch } from '../store';
import type { NotificationCounts } from '../actions';

// Minimal interface for the realtime push client — IApiClient from @shore/types does not type this.
export interface RealtimeChannel {
  bind(eventName: string, fn: (data: unknown) => void): void;
}

export interface RealtimeClient {
  connect(): Promise<string>;
  subscribe(channel: string): RealtimeChannel;
}

// Minimal interface for the notifications API — notificationsV2 is not typed in @shore/types.
interface NotificationsApiClient {
  notificationsV2: {
    unread(params: { merchant_id: string }): Promise<NotificationCounts>;
  };
  realtime: RealtimeClient;
}

export function observeEscKey(store: { dispatch: AppDispatch }): void {
  addEventListener('keydown', (event: Event) => {
    const { key, keyCode } = event as KeyboardEvent;
    if (key === 'Escape' || keyCode === 27) {
      store.dispatch(hitEscape());
    }
  });
}

export function observeDocumentHeaders(store: {
  dispatch: AppDispatch;
}): Promise<void> {
  const url = getDocumentUrl(true);
  const update = () =>
    fetchHeaders(url, ['last-modified', 'date']).then(
      ([lastModified, date]) => {
        store.dispatch(receiveDocumentHeaders({ lastModified, date }));
      }
    );

  setInterval(update, CHECK_FOR_UPDATES_INTERVAL);
  return update();
}

/* istanbul ignore next */
export async function observeUnreadNotificationCount(
  store: { dispatch: AppDispatch },
  { apiClient }: { apiClient: NotificationsApiClient },
  merchantId: string
): Promise<void> {
  const update =
    (
      client: NotificationsApiClient,
      dispatch: AppDispatch,
      action: typeof newNotificationCounts
    ) =>
    async () => {
      if (!document.hidden) {
        // TODO replace with web socket
        const unreadNotifications = await client.notificationsV2.unread({
          merchant_id: merchantId,
        });

        dispatch(action(unreadNotifications));
      }
    };

  setInterval(
    update(apiClient, store.dispatch, newNotificationCounts),
    CHECK_FOR_NOTIFICATION_COUNT_INTERVAL
  );
}

export const observeDocumentClick = ({
  dispatch,
}: {
  dispatch: AppDispatch;
}): void => {
  document.addEventListener('click', () => {
    dispatch(documentClick());
  });
};

export const websocketListener =
  (apiClient: { realtime: RealtimeClient }) =>
  (merchantId: string) =>
  (channelName: string) =>
  (eventName: string) =>
  (fn: (data: unknown) => void): void => {
    const getChannel = (name: string, id: string) => `${name}=${id}`;

    apiClient.realtime.connect().then((state) => {
      if (state === 'connected') {
        const channel = apiClient.realtime.subscribe(
          getChannel(channelName, merchantId)
        );
        channel.bind(eventName, fn);
      } else {
        // TODO: post error to sentry
        console.log(`Failed to bind to channel ${channelName}`);
      }
    });
  };
